import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';
import {
  getAdminUserById,
  updateAdminUser,
  deactivateAdminUser,
  activateAdminUser,
} from '../../api/adminUsers';
import { parseServerDate } from '../../utils/time';
import TariffDialog from './TariffDialog';

export const USER_TYPES = [
  { value: 'individual', label: 'Физическое лицо', badge: 'bg-gray-500/15 text-gray-500 border-gray-500/30 dark:text-gray-300' },
  { value: 'business', label: 'Бизнес', badge: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  { value: 'support', label: 'Поддержка', badge: 'bg-teal-500/15 text-teal-500 border-teal-500/30' },
  { value: 'operator', label: 'Оператор', badge: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  { value: 'sales_manager', label: 'Менеджер продаж', badge: 'bg-sky-500/15 text-sky-500 border-sky-500/30' },
  { value: 'moderator', label: 'Модератор', badge: 'bg-orange-500/15 text-orange-500 border-orange-500/30' },
  { value: 'admin', label: 'Администратор', badge: 'bg-rose-500/15 text-rose-500 border-rose-500/30' },
];
const STAFF_TYPES = ['support', 'operator', 'sales_manager', 'moderator', 'admin'];

export const BUSINESS_ROLES = [
  { value: 'director', label: 'Директор' },
  { value: 'financier', label: 'Финансист' },
  { value: 'manager', label: 'Менеджер' },
];

const ANNOUNCEMENT_STATUS = {
  active: { label: 'Активные', cls: 'text-emerald-500' },
  pending: { label: 'На модерации', cls: 'text-amber-500' },
  rejected: { label: 'Отклонённые', cls: 'text-rose-500' },
  inactive: { label: 'Неактивные', cls: 'text-text-muted' },
};

const TRANSACTION_TYPES = {
  BALANCE_TOP_UP: 'Пополнение',
  PROMOTION_SPENDING: 'Продвижение',
  REFUND: 'Возврат',
  ORDER_PAYMENT: 'Оплата заказа',
  COMMISSION: 'Комиссия',
  BUSINESS_CREDIT: 'Бизнес-кредит',
  WITHDRAWAL_REQUEST: 'Заявка на вывод',
  WITHDRAWAL_COMPLETED: 'Вывод средств',
  WITHDRAWAL_REJECTED: 'Вывод отклонён',
  SUBSCRIPTION_PURCHASE: 'Подписка',
};

export const typeMeta = (type) =>
  USER_TYPES.find((t) => t.value === type) || { value: type, label: type || '—', badge: USER_TYPES[0].badge };
export const roleLabel = (role) => BUSINESS_ROLES.find((r) => r.value === role)?.label || role;

const AVATAR_COLORS = ['bg-indigo-500', 'bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500', 'bg-teal-500', 'bg-fuchsia-500'];

export const UserAvatar = ({ user, size = 'h-10 w-10 text-sm' }) =>
  user.avatar_url ? (
    <img src={user.avatar_url} alt="" className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <div
      className={`${size} ${AVATAR_COLORS[(user.id || 0) % AVATAR_COLORS.length]} flex shrink-0 items-center justify-center rounded-full font-semibold text-white`}
    >
      {(user.name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?'}
    </div>
  );

export const TypeBadge = ({ type }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${typeMeta(type).badge}`}>
    {typeMeta(type).label}
  </span>
);

const inputCls =
  'w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

const fmtMoney = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₸`;
const fmtDate = (iso, withTime = false) =>
  iso
    ? parseServerDate(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
      })
    : '—';

const relative = (iso) => {
  if (!iso) return null;
  const diff = (Date.now() - parseServerDate(iso).getTime()) / 1000;
  if (diff < 300) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} дн назад`;
  return fmtDate(iso);
};

const humanizeError = (err) => {
  const d = err?.detail || err?.message || '';
  if (/email already used/i.test(d)) return 'Этот email уже используется другим пользователем.';
  if (/phone_number already used/i.test(d)) return 'Этот телефон уже используется другим пользователем.';
  if (/already in use/i.test(d)) return 'Email или телефон уже используется.';
  if (/Only admins/i.test(d)) return 'Только администратор может менять сотрудников и назначать служебные роли.';
  if (/own account/i.test(d)) return 'Нельзя деактивировать собственный аккаунт.';
  if (/System users/i.test(d)) return 'Системных пользователей нельзя изменять.';
  if (/Invalid phone/i.test(d)) return 'Некорректный номер телефона.';
  if (/Invalid email/i.test(d)) return 'Некорректный email.';
  if (/Director not found/i.test(d)) return 'Директор с таким ID не найден.';
  return d || 'Не удалось выполнить действие.';
};

const Copy = ({ value }) => {
  const [done, setDone] = useState(false);
  if (value === null || value === undefined || value === '') return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(value));
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard недоступен */
        }
      }}
      aria-label="Скопировать"
      title="Скопировать"
      className="rounded p-0.5 text-text-muted hover:text-text-primary"
    >
      {done ? <CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
    </button>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">{title}</h4>
    <dl className="divide-y divide-border-primary">{children}</dl>
  </section>
);

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <dt className="shrink-0 text-sm text-text-secondary">{label}</dt>
    <dd className="flex min-w-0 items-center justify-end gap-1 text-right text-sm font-medium text-text-primary">{children}</dd>
  </div>
);

const YesNo = ({ value }) =>
  value ? <span className="text-emerald-500">Да</span> : <span className="text-text-muted">Нет</span>;

const Toggle = ({ checked, onChange, label, hint }) => (
  <label className="flex cursor-pointer items-start justify-between gap-3 py-2">
    <span>
      <span className="block text-sm text-text-primary">{label}</span>
      {hint && <span className="block text-xs text-text-muted">{hint}</span>}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#4F45E5]' : 'bg-bg-tertiary ring-1 ring-border-primary'}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  </label>
);

const toForm = (u) => ({
  name: u.name || '',
  email: u.email || '',
  phone_number: u.phone_number || '',
  user_type: u.user_type || 'individual',
  business_role: u.business_role || '',
  business_director_id: u.business_director_id ? String(u.business_director_id) : '',
  rating: u.rating ?? 0,
  is_verified: Boolean(u.is_verified),
  is_vat_payer: Boolean(u.is_vat_payer),
  business_terms_accepted: Boolean(u.business_terms_accepted),
});

/** Отправляем только изменённые поля — PATCH не затирает то, что правили параллельно. */
const diffPayload = (form, user) => {
  const initial = toForm(user);
  const out = {};
  Object.keys(form).forEach((key) => {
    if (form[key] === initial[key]) return;
    if (key === 'rating') out.rating = Number(form.rating);
    else if (key === 'business_director_id') out.business_director_id = form.business_director_id ? Number(form.business_director_id) : null;
    else if (key === 'business_role') out.business_role = form.business_role || null;
    else out[key] = typeof form[key] === 'string' ? form[key].trim() : form[key];
  });
  if (out.user_type && out.user_type !== 'business') {
    out.business_role = null;
    out.business_director_id = null;
  }
  return out;
};

const UserDetailsPanel = ({ userId, onClose, onUpdated, onOpenUser, notify }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [state, setState] = useState('loading');
  const [tab, setTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [tariffDialog, setTariffDialog] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const data = await getAdminUserById(userId);
      setUser(data);
      setState('ready');
    } catch (err) {
      console.error('Error loading user:', err);
      setState(err.status === 404 ? 'notfound' : 'error');
    }
  }, [userId]);

  useEffect(() => {
    setEditing(false);
    setTab('profile');
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || statusDialog || tariffDialog) return;
      if (editing) setEditing(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, statusDialog, tariffDialog, onClose]);

  const startEdit = () => {
    setForm(toForm(user));
    setFormError(null);
    setEditing(true);
  };

  const payload = useMemo(() => (editing && form && user ? diffPayload(form, user) : {}), [editing, form, user]);
  const dirty = Object.keys(payload).length > 0;

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) {
      setEditing(false);
      return;
    }
    if (form.user_type === 'business' && form.business_role && form.business_role !== 'director' && !form.business_director_id) {
      setFormError('Для сотрудника бизнеса укажите ID директора.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await updateAdminUser(user.id, payload);
      const updated = res.user || { ...user, ...payload };
      setUser(updated);
      setEditing(false);
      onUpdated?.(updated);
      notify?.('success', 'Изменения сохранены');
    } catch (err) {
      console.error('Error updating user:', err);
      setFormError(humanizeError(err));
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  if (state === 'loading' && !user) {
    return (
      <div className="animate-pulse space-y-4 p-5">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-full bg-bg-tertiary" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/2 rounded bg-bg-tertiary" />
            <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
          </div>
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-4 rounded bg-bg-tertiary" />
        ))}
      </div>
    );
  }

  if (state === 'error' || state === 'notfound') {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
        <p className="text-sm text-text-secondary">
          {state === 'notfound' ? `Пользователь #${userId} не найден` : 'Не удалось загрузить пользователя'}
        </p>
        <div className="flex gap-2">
          {state === 'error' && (
            <button type="button" onClick={load} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
              Повторить
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  const isBusiness = (editing ? form.user_type : user.user_type) === 'business';
  const announcements = user.announcements || { total: 0, by_status: {} };
  const onlineNow = !user.last_online && user.last_active_at && Date.now() - parseServerDate(user.last_active_at).getTime() < 5 * 60 * 1000;
  const assigningStaff = editing && form.user_type !== user.user_type && STAFF_TYPES.includes(form.user_type);

  return (
    <div className={state === 'loading' ? 'pointer-events-none opacity-60' : ''}>
      {/* Header */}
      <div className="flex items-start gap-3 border-b border-border-primary p-5">
        <div className="relative">
          <UserAvatar user={user} size="h-14 w-14 text-lg" />
          {onlineNow && <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-emerald-500 ring-2 ring-bg-card" title="В сети" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-lg font-semibold text-text-primary">
              {user.name || <span className="italic text-text-muted">Без имени</span>}
            </h3>
            {user.is_verified && <CheckBadgeIcon className="h-5 w-5 shrink-0 text-sky-500" title="Верифицирован" />}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <TypeBadge type={user.user_type} />
            {user.user_type === 'business' && user.business_role && (
              <span className="inline-flex items-center rounded-md border border-blue-500/30 px-2 py-0.5 text-xs font-medium text-blue-400">
                {roleLabel(user.business_role)}
              </span>
            )}
            {!user.is_active && (
              <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-xs font-semibold text-rose-500">
                <NoSymbolIcon className="h-3.5 w-3.5" /> Деактивирован
              </span>
            )}
            {user.is_vip_seller && (
              <span className="inline-flex items-center rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-500">VIP</span>
            )}
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {onlineNow ? 'В сети' : user.last_active_at ? `Был(а) активен ${relative(user.last_active_at)}` : 'Нет данных об активности'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={load} aria-label="Обновить" title="Обновить" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
            <ArrowPathIcon className={`h-4 w-4 ${state === 'loading' ? 'animate-spin' : ''}`} />
          </button>
          <button type="button" onClick={onClose} aria-label="Закрыть (Esc)" title="Закрыть (Esc)" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {!user.is_active && !editing && (
        <div className="flex items-center justify-between gap-3 border-b border-rose-500/20 bg-rose-500/[0.07] px-5 py-3">
          <p className="text-sm text-rose-500">Аккаунт деактивирован — вход в приложение запрещён.</p>
          <button
            type="button"
            onClick={() => setStatusDialog(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-500 hover:bg-emerald-500/10"
          >
            <LockOpenIcon className="h-4 w-4" /> Активировать
          </button>
        </div>
      )}

      {editing ? (
        <form onSubmit={save} className="space-y-5 p-5">
          <section className="space-y-3">
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Основное</h4>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Имя</span>
              <input autoFocus type="text" value={form.name} onChange={(e) => set('name')(e.target.value)} className={inputCls} />
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">Телефон</span>
                <input type="tel" value={form.phone_number} onChange={(e) => set('phone_number')(e.target.value)} placeholder="+77011234567" className={`${inputCls} font-mono`} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">Email</span>
                <input type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} placeholder="—" className={inputCls} />
              </label>
            </div>
            {form.phone_number !== (user.phone_number || '') && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                Телефон — это логин пользователя. После смены он будет входить по новому номеру.
              </p>
            )}
          </section>

          <section className="space-y-3">
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Роль</h4>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Тип аккаунта</span>
              <select value={form.user_type} onChange={(e) => set('user_type')(e.target.value)} className={inputCls}>
                {USER_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            {isBusiness && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">Роль в бизнесе</span>
                  <select value={form.business_role} onChange={(e) => set('business_role')(e.target.value)} className={inputCls}>
                    <option value="">— Не задана —</option>
                    {BUSINESS_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>
                {form.business_role && form.business_role !== 'director' && (
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-text-secondary">ID директора</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.business_director_id}
                      onChange={(e) => set('business_director_id')(e.target.value.replace(/\D/g, ''))}
                      className={`${inputCls} font-mono`}
                    />
                  </label>
                )}
              </div>
            )}
            {assigningStaff && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                Служебная роль даёт доступ к админ-панели. Назначать её может только администратор.
              </p>
            )}
          </section>

          <section>
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Статусы</h4>
            <div className="divide-y divide-border-primary">
              <Toggle checked={form.is_verified} onChange={set('is_verified')} label="Верифицирован" hint="Бизнес прошёл KYC-проверку" />
              <Toggle checked={form.is_vat_payer} onChange={set('is_vat_payer')} label="Плательщик НДС" hint="НДС добавляется к бизнес-заказам" />
              <Toggle checked={form.business_terms_accepted} onChange={set('business_terms_accepted')} label="Принял условия для бизнеса" />
            </div>
            <label className="mt-2 block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Рейтинг (0–5)</span>
              <input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={(e) => set('rating')(e.target.value)} className={`${inputCls} w-32`} />
            </label>
          </section>

          {formError && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-sm text-rose-500">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              {formError}
            </div>
          )}

          <div className="sticky bottom-0 -mx-5 -mb-5 flex gap-2 border-t border-border-primary bg-bg-card px-5 py-3">
            <button
              type="submit"
              disabled={saving || !dirty}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {dirty ? `Сохранить (${Object.keys(payload).length})` : 'Нет изменений'}
            </button>
            <button type="button" onClick={() => setEditing(false)} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
              Отмена
            </button>
          </div>
        </form>
      ) : (
        <>
          <div role="tablist" className="flex gap-4 border-b border-border-primary px-5">
            {[
              { id: 'profile', label: 'Профиль' },
              { id: 'activity', label: `Активность` },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`-mb-px border-b-2 py-2.5 text-sm font-medium transition-colors ${
                  tab === t.id ? 'border-[#4F45E5] text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'profile' ? (
            <div className="space-y-5 p-5">
              <Section title="Контакты">
                <Row label="ID"><span className="font-mono">#{user.id}</span><Copy value={user.id} /></Row>
                <Row label="Телефон"><span className="truncate font-mono">{user.phone_number || '—'}</span><Copy value={user.phone_number} /></Row>
                <Row label="Email"><span className="truncate">{user.email || '—'}</span><Copy value={user.email} /></Row>
              </Section>

              <Section title="Аккаунт">
                <Row label="Статус">
                  {user.is_active ? <span className="text-emerald-500">Активен</span> : <span className="text-rose-500">Деактивирован</span>}
                </Row>
                <Row label="Зарегистрирован">{fmtDate(user.created_at, true)}</Row>
                <Row label="Последняя активность">{user.last_active_at ? fmtDate(user.last_active_at, true) : '—'}</Row>
                {user.user_type === 'business' && (
                  <>
                    <Row label="Роль в бизнесе">{user.business_role ? roleLabel(user.business_role) : '—'}</Row>
                    {user.director && (
                      <Row label="Директор">
                        <button type="button" onClick={() => onOpenUser?.(user.director.id)} className="truncate text-[#7C3AED] hover:underline dark:text-indigo-300">
                          {user.director.name || 'Без имени'} #{user.director.id}
                        </button>
                      </Row>
                    )}
                    {user.employees_count > 0 && <Row label="Сотрудников">{user.employees_count}</Row>}
                  </>
                )}
              </Section>

              <Section title="Финансы и подписка">
                <Row label="Баланс">
                  <span className="text-emerald-500">{fmtMoney(user.balance)}</span>
                  <button
                    type="button"
                    onClick={() => navigate(`/balance?user=${user.id}`)}
                    className="ml-2 rounded-md border border-border-primary px-2 py-0.5 text-xs font-medium text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
                  >
                    Пополнить
                  </button>
                </Row>
                <Row label="Рейтинг">★ {Number(user.rating || 0).toFixed(1)}</Row>
                <Row label="Тариф">
                  {user.subscription?.plan ? (
                    <span className="truncate">
                      {user.subscription.plan.name}
                      {user.subscription.expires_at && <span className="ml-1 font-normal text-text-muted">до {fmtDate(user.subscription.expires_at)}</span>}
                    </span>
                  ) : (
                    'Бесплатный'
                  )}
                  <button
                    type="button"
                    onClick={() => setTariffDialog(true)}
                    className="ml-2 shrink-0 rounded-md border border-border-primary px-2 py-0.5 text-xs font-medium text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
                  >
                    Изменить
                  </button>
                </Row>
                {Number(user.subscription?.vip_hours_balance) > 0 && <Row label="VIP-часы">{user.subscription.vip_hours_balance}</Row>}
                {user.is_vip_seller && <Row label="VIP-продавец">до {fmtDate(user.vip_seller_expires_at)}</Row>}
              </Section>

              {(user.user_type === 'business' || user.is_verified || user.is_vat_payer) && (
                <Section title="Бизнес">
                  <Row label="Верифицирован"><YesNo value={user.is_verified} /></Row>
                  <Row label="Плательщик НДС"><YesNo value={user.is_vat_payer} /></Row>
                  <Row label="Принял условия"><YesNo value={user.business_terms_accepted} /></Row>
                  {user.converted_from_business && <Row label="Перенесён из юр. лица"><YesNo value /></Row>}
                </Section>
              )}

              {(user.bank_details || user.json_data) && (
                <section>
                  <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Дополнительные данные</h4>
                  {[
                    ['Банковские реквизиты', user.bank_details],
                    ['json_data', user.json_data],
                  ]
                    .filter(([, v]) => v && Object.keys(v).length)
                    .map(([label, value]) => (
                      <details key={label} className="mb-2 rounded-lg border border-border-primary">
                        <summary className="cursor-pointer px-3 py-2 text-sm text-text-secondary hover:text-text-primary">{label}</summary>
                        <pre className="max-h-60 overflow-auto border-t border-border-primary px-3 py-2 text-xs text-text-primary">{JSON.stringify(value, null, 2)}</pre>
                      </details>
                    ))}
                </section>
              )}
            </div>
          ) : (
            <div className="space-y-5 p-5">
              <section>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Объявления · {announcements.total}</h4>
                {announcements.total ? (
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(ANNOUNCEMENT_STATUS).map(([key, meta]) => (
                      <div key={key} className="rounded-lg border border-border-primary px-3 py-2">
                        <div className={`text-lg font-bold tabular-nums ${meta.cls}`}>{announcements.by_status?.[key] || 0}</div>
                        <div className="text-xs text-text-secondary">{meta.label}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-text-muted">Нет объявлений</p>
                )}
              </section>

              <section>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Активные сессии · {user.sessions?.length || 0}</h4>
                {user.sessions?.length ? (
                  <ul className="divide-y divide-border-primary rounded-lg border border-border-primary">
                    {user.sessions.map((s) => {
                      const Icon = s.platform === 'web' ? ComputerDesktopIcon : DevicePhoneMobileIcon;
                      return (
                        <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                          <Icon className="h-5 w-5 shrink-0 text-text-muted" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-text-primary">
                              {s.device_model || s.platform || 'Устройство'}
                              {s.os_version && <span className="text-text-muted"> · {s.os_version}</span>}
                            </p>
                            <p className="truncate text-xs text-text-muted">
                              {s.app_version && `v${s.app_version} · `}
                              {s.ip || 'IP неизвестен'}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs text-text-muted">{relative(s.last_seen_at)}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-text-muted">Нет активных сессий</p>
                )}
              </section>

              <section>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Последние транзакции</h4>
                {user.recent_transactions?.length ? (
                  <ul className="divide-y divide-border-primary rounded-lg border border-border-primary">
                    {user.recent_transactions.map((t) => (
                      <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm text-text-primary">{TRANSACTION_TYPES[t.type] || t.type}</p>
                          <p className="text-xs text-text-muted">{fmtDate(t.created_at, true)}</p>
                        </div>
                        <span className={`shrink-0 text-sm font-semibold tabular-nums ${t.amount >= 0 ? 'text-emerald-500' : 'text-text-primary'}`}>
                          {t.amount > 0 ? '+' : ''}
                          {fmtMoney(t.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-text-muted">Транзакций нет</p>
                )}
              </section>
            </div>
          )}

          <div className="flex gap-2 border-t border-border-primary p-5">
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-tertiary"
            >
              <PencilSquareIcon className="h-4 w-4" /> Редактировать
            </button>
            {user.is_active && (
              <button
                type="button"
                onClick={() => setStatusDialog(true)}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-500/40 px-4 py-2 text-sm font-medium text-rose-500 hover:bg-rose-500/10"
              >
                <NoSymbolIcon className="h-4 w-4" /> Деактивировать
              </button>
            )}
          </div>
        </>
      )}

      {tariffDialog && (
        <TariffDialog
          user={user}
          onCancel={() => setTariffDialog(false)}
          onDone={(updated, message) => {
            setTariffDialog(false);
            setUser(updated);
            onUpdated?.(updated);
            notify?.('success', message);
          }}
        />
      )}

      {statusDialog && (
        <StatusDialog
          user={user}
          onCancel={() => setStatusDialog(false)}
          onDone={(updated, message) => {
            setStatusDialog(false);
            setUser(updated);
            onUpdated?.(updated);
            notify?.('success', message);
            load();
          }}
        />
      )}
    </div>
  );
};

const StatusDialog = ({ user, onCancel, onDone }) => {
  const deactivating = user.is_active;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (deactivating) {
        const res = await deactivateAdminUser(user.id, reason.trim());
        onDone(
          { ...user, is_active: false, sessions: [] },
          `Аккаунт #${user.id} деактивирован${res.revoked_sessions ? `, завершено сессий: ${res.revoked_sessions}` : ''}`
        );
      } else {
        await activateAdminUser(user.id);
        onDone({ ...user, is_active: true }, `Аккаунт #${user.id} снова активен`);
      }
    } catch (err) {
      console.error('Status change failed:', err);
      setError(humanizeError(err));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}
      onKeyDown={(e) => e.key === 'Escape' && !busy && onCancel()}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-block border border-border-primary bg-bg-card p-5 text-left shadow-lg">
        <div className="flex gap-3">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${deactivating ? 'bg-rose-500/15 text-rose-500' : 'bg-emerald-500/15 text-emerald-500'}`}>
            {deactivating ? <NoSymbolIcon className="h-5 w-5" /> : <ShieldCheckIcon className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-text-primary">
              {deactivating ? 'Деактивировать аккаунт?' : 'Активировать аккаунт?'}
            </h3>
            <p className="mt-0.5 text-sm text-text-secondary">
              {user.name || 'Без имени'} · <span className="font-mono">#{user.id}</span>
            </p>
          </div>
        </div>

        {deactivating ? (
          <>
            <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-text-secondary">
              <li>вход в приложение и на сайт будет запрещён;</li>
              <li>все активные сессии ({user.sessions?.length || 0}) будут завершены сразу;</li>
              <li>данные, объявления и переписка сохранятся — аккаунт можно активировать обратно.</li>
            </ul>
            <label className="mt-4 block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Причина (необязательно, сохраняется в журнале сессий)</span>
              <textarea
                autoFocus
                rows={2}
                maxLength={100}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Например: мошенничество, спам, просьба пользователя"
                className={inputCls}
              />
            </label>
          </>
        ) : (
          <p className="mt-4 text-sm text-text-secondary">Пользователь снова сможет входить в приложение по SMS-коду.</p>
        )}

        {error && <p className="mt-3 text-sm text-rose-500">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
            Отмена
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${deactivating ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          >
            {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {deactivating ? 'Деактивировать' : 'Активировать'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsPanel;
